import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Flex,
  Text,
  Icon,
  IconButton,
  Spinner,
  Input,
} from '@chakra-ui/react';
import {
  DataTable,
  StatusBadge,
  Button as ButtonComponent,
  type Column,
  type StatusType,
} from '@themes/aegix';
import {
  LuUpload,
  LuTrash2,
  LuRefreshCw,
  LuFileImage,
  LuPencil,
  LuCheck,
  LuX,
} from 'react-icons/lu';
import type { BlueprintStatus } from '../../../services';
import { toaster } from '../../ui/toaster';

export type BlueprintViewStatus = BlueprintStatus | 'staged' | 'uploading';

export interface BlueprintRowVM {
  [key: string]: unknown;
  id: string;
  label: string;
  fileName: string;
  status: BlueprintViewStatus;
  errorMessage?: string;
}

export interface BlueprintsTableViewProps {
  rows: BlueprintRowVM[];
  isLoading?: boolean;
  isError?: boolean;
  addLabel?: string;
  addDisabled?: boolean;
  onAddFileClick: () => void;
  onEditLabel: (id: string, label: string) => void;
  onReplaceClick: (id: string) => void;
  onDelete: (id: string) => void;
  editingDisabled?: boolean;
  replaceDisabled?: boolean;
  deleteDisabled?: boolean;
  helperText?: string;
}

export function BlueprintsTableView({
  rows,
  isLoading,
  isError,
  addLabel,
  addDisabled,
  onAddFileClick,
  onEditLabel,
  onReplaceClick,
  onDelete,
  editingDisabled,
  replaceDisabled,
  deleteDisabled,
  helperText,
}: BlueprintsTableViewProps) {
  const { t } = useTranslation();
  const defaultAddLabel = addLabel ?? t('sites.addBlueprint', 'Add Blueprint');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [labelDraft, setLabelDraft] = useState('');

  const statusToBadge = (status: BlueprintViewStatus): {
    type: StatusType;
    label: string;
  } => {
    switch (status) {
      case 'submitted':
        return { type: 'processing', label: t('sites.statusSubmitted', 'Submitted') };
      case 'complete':
        return { type: 'completed', label: t('sites.statusComplete', 'Complete') };
      case 'failed':
        return { type: 'failed', label: t('sites.statusFailed', 'Failed') };
      case 'uploading':
        return { type: 'processing', label: t('sites.uploading', 'Uploading') };
      case 'staged':
        return { type: 'pending', label: t('sites.statusStaged', 'Staged') };
      case 'pending':
      default:
        return { type: 'pending', label: t('common.pending', 'Pending') };
    }
  };

  const startEdit = (row: BlueprintRowVM) => {
    setEditingId(row.id);
    setLabelDraft(row.label);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setLabelDraft('');
  };

  const saveLabel = (id: string) => {
    const next = labelDraft.trim();
    if (!next) {
      toaster.create({
        title: t('sites.labelRequired', 'Label required'),
        description: t('sites.blueprintLabelCannotBeEmpty', 'Blueprint label cannot be empty.'),
        type: 'error',
        duration: 3000,
      });
      return;
    }
    onEditLabel(id, next);
    setEditingId(null);
    setLabelDraft('');
  };

  const columns: Column<BlueprintRowVM>[] = useMemo(
    () => [
      {
        key: 'label',
        label: t('common.label', 'Label'),
        render: (row) => {
          if (editingId === row.id) {
            return (
              <Flex align="center" gap={2} minW={0}>
                <Input
                  size="sm"
                  value={labelDraft}
                  onChange={(e) => setLabelDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      saveLabel(row.id);
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      cancelEdit();
                    }
                  }}
                  // eslint-disable-next-line jsx-a11y/no-autofocus -- inline rename: this input only exists after the user clicks Edit on a row, so focus follows their action into a newly-revealed control rather than stealing it on page load
                  autoFocus
                  aria-label={t('sites.blueprintLabelAria', 'Blueprint label')}
                  data-e2e="input-blueprint-label"
                />
                <IconButton
                  aria-label={t('sites.saveLabelAria', 'Save label')}
                  size="xs"
                  variant="ghost"
                  onClick={() => saveLabel(row.id)}
                  data-e2e="button-save-blueprint"
                >
                  <LuCheck />
                </IconButton>
                <IconButton
                  aria-label={t('sites.cancelRenameAria', 'Cancel rename')}
                  size="xs"
                  variant="ghost"
                  onClick={cancelEdit}
                  data-e2e="button-cancel-blueprint"
                >
                  <LuX />
                </IconButton>
              </Flex>
            );
          }
          return (
            <Flex align="center" gap={2} minW={0}>
              <Icon size="sm" color="text.secondary">
                <LuFileImage />
              </Icon>
              <Flex direction="column" minW={0}>
                <Text fontSize="sm" truncate data-e2e="text-blueprint-label">
                  {row.label}
                </Text>
                {row.fileName && row.fileName !== row.label && (
                  <Text fontSize="xs" color="text.tertiary" truncate data-e2e={`${row.label}-filename`}>
                    {row.fileName}
                  </Text>
                )}
              </Flex>
            </Flex>
          );
        },
      },
      {
        key: 'status',
        label: t('common.status', 'Status'),
        width: '150px',
        render: (row) => {
          const badge = statusToBadge(row.status);
          const inFlight =
            row.status === 'pending' ||
            row.status === 'submitted' ||
            row.status === 'uploading';
          return (
            <Flex align="center" gap={2}>
              {inFlight && <Spinner size="xs" color="blue.500" />}
              <StatusBadge status={badge.type} label={badge.label} />
            </Flex>
          );
        },
      },
    ],
    [editingId, labelDraft, t],
  );

  const renderRowActions = (row: BlueprintRowVM) => {
    if (editingId === row.id) {
      return <Box />;
    }
    return (
      <Flex gap={1} justifyContent="flex-end">
        <IconButton
          aria-label={t('sites.editBlueprintLabelAria', 'Edit blueprint label')}
          size="xs"
          variant="ghost"
          title={t('sites.editLabelTitle', 'Edit label')}
          onClick={() => startEdit(row)}
          disabled={editingDisabled}
          data-e2e={`${row.label}-edit`}
        >
          <LuPencil />
        </IconButton>
        <IconButton
          aria-label={t('sites.replaceBlueprintFileAria', 'Replace blueprint file')}
          size="xs"
          variant="ghost"
          title={t('sites.replaceFileTitle', 'Replace file')}
          onClick={() => onReplaceClick(row.id)}
          disabled={replaceDisabled}
          data-e2e={`${row.label}-replace`}
        >
          <LuRefreshCw />
        </IconButton>
        <IconButton
          aria-label={t('sites.removeBlueprintAria', 'Remove blueprint')}
          size="xs"
          variant="ghost"
          title={t('common.delete', 'Delete')}
          onClick={() => onDelete(row.id)}
          disabled={deleteDisabled}
          data-e2e={`${row.label}-delete`}
        >
          <LuTrash2 />
        </IconButton>
      </Flex>
    );
  };

  return (
    <Box>
      <Flex justify="flex-end" mb={3}>
        <ButtonComponent
          visual="primary"
          size="sm"
          onClick={onAddFileClick}
          disabled={addDisabled}
        >
          {defaultAddLabel}
        </ButtonComponent>
      </Flex>

      {isLoading ? (
        <Flex py={6} justify="center">
          <Spinner size="sm" color="button.primary" />
        </Flex>
      ) : isError ? (
        <Flex py={6} justify="center">
          <Text fontSize="sm" color="text.danger">
            {t('sites.failedLoadBlueprints', 'Failed to load blueprints.')}
          </Text>
        </Flex>
      ) : rows.length === 0 ? (
        <Flex
          direction="column"
          align="center"
          justify="center"
          py={6}
          gap={2}
          border="1px dashed"
          borderColor="border.secondary"
          borderRadius="md"
        >
          <Icon size="md" color="text.secondary">
            <LuUpload />
          </Icon>
          <Text fontSize="sm" color="text.secondary">
            {t('sites.noBlueprintsYet', 'No blueprints yet.')}
          </Text>
          <Text fontSize="xs" color="text.tertiary">
            {t('sites.blueprintsAcceptedTypes', '.png, .jpg, .pdf up to 25MB')}
          </Text>
        </Flex>
      ) : (
        <Box>
          <DataTable
            columns={columns}
            data={rows}
            keyExtractor={(row) => row.id}
            renderRowActions={renderRowActions}
          />
          {rows.some((r) => r.errorMessage) && (
            <Box mt={2}>
              {rows
                .filter((r) => r.errorMessage)
                .map((r) => (
                  <Text key={r.id} fontSize="xs" color="text.danger" data-e2e={`${r.label}-error`}>
                    {r.label}: {r.errorMessage}
                  </Text>
                ))}
            </Box>
          )}
          {helperText && (
            <Text fontSize="xs" color="text.tertiary" mt={2} data-e2e="helper-text">
              {helperText}
            </Text>
          )}
        </Box>
      )}
    </Box>
  );
}
